import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildQueue, gradeCard, previewIntervals, RATING_LABELS, type RatingValue } from "../lib/scheduler";
import { useStore } from "../lib/store";
import * as sync from "../lib/sync";
import * as tts from "../lib/tts";
import type { ReviewItem } from "../lib/types";

interface Props {
  sourceId: string | null;
  domain: string | null;
  onExit: () => void;
}

export default function Review({ sourceId, domain, onExit }: Props) {
  const store = useStore();
  const { cards, notes, lang, profile, counts, bumpCounts, reload } = store;

  // The queue is built once per session. Rebuilding it as answers land would
  // reshuffle the deck under the reviewer's feet.
  const [queue, setQueue] = useState<ReviewItem[] | null>(null);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [again, setAgain] = useState<ReviewItem[]>([]);
  const shownAt = useRef(Date.now());

  useEffect(() => {
    if (queue !== null) return;
    setQueue(buildQueue(cards, notes, { lang, profile, counts, sourceId, domain }));
  }, [queue, cards, notes, lang, profile, counts, sourceId, domain]);

  const current = queue ? queue[index] ?? again[index - queue.length] : undefined;
  const total = (queue?.length ?? 0) + again.length;

  useEffect(() => {
    shownAt.current = Date.now();
    setRevealed(false);
  }, [index]);

  // Speak the target language automatically where the profile asks for it, and
  // never on the production front — that would read out the answer.
  useEffect(() => {
    if (!current || !profile.audioEnabled || !profile.autoplayAudio) return;
    const speakable = speakableText(current, revealed);
    if (speakable) tts.speak(speakable, lang, { voiceURI: profile.voiceURI });
  }, [current, revealed, profile.audioEnabled, profile.autoplayAudio, profile.voiceURI, lang]);

  const answer = useCallback(
    async (rating: RatingValue) => {
      if (!current) return;
      const wasNew = current.card.reps === 0;
      const result = gradeCard(current.card, rating, profile, new Date(), Date.now() - shownAt.current);
      await sync.recordAnswer(result.card, result.review);
      bumpCounts(wasNew ? "new" : "review");

      // "Again" puts the card back at the end of this session as well as
      // rescheduling it — a word just failed should be seen once more today.
      if (rating === 1 && !result.becameLeech) {
        setAgain((list) => [...list, { ...current, card: result.card }]);
      }
      setIndex((i) => i + 1);
      tts.stop();
    },
    [current, profile, bumpCounts],
  );

  const finish = useCallback(async () => {
    tts.stop();
    await reload();
    void store.syncNow();
    onExit();
  }, [reload, store, onExit]);

  // Keyboard shortcuts make desktop review fast; the phone uses the buttons.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (!revealed && (event.key === " " || event.key === "Enter")) {
        event.preventDefault();
        setRevealed(true);
        return;
      }
      if (revealed && event.key >= "1" && event.key <= "4") {
        event.preventDefault();
        void answer(Number(event.key) as RatingValue);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [revealed, answer]);

  const intervals = useMemo(
    () => (current ? previewIntervals(current.card, profile) : null),
    [current, profile],
  );

  if (queue === null) {
    return <div className="empty"><span className="spinner" /></div>;
  }

  if (!current) {
    const done = index;
    return (
      <div className="empty">
        <div className="big">{done > 0 ? "✓" : "—"}</div>
        <p>
          {done > 0
            ? `${done} card${done === 1 ? "" : "s"} reviewed.`
            : "Nothing due here right now."}
        </p>
        <button className="btn primary" onClick={finish}>Done</button>
      </div>
    );
  }

  const { card, note } = current;
  const progress = total > 0 ? Math.min(1, index / total) : 0;

  return (
    <div className="reviewscreen">
      <div className="progress"><div style={{ width: `${progress * 100}%` }} /></div>

      <div className="prompt">
        <span className="direction">
          {card.direction === "cloze"
            ? note.cloze_hint ?? "Fill the blank"
            : card.direction === "production"
              ? `English → ${lang === "fr" ? "French" : "Spanish"}`
              : `${lang === "fr" ? "French" : "Spanish"} → English`}
          {card.reps === 0 ? " · new" : ""}
        </span>

        {card.direction === "cloze" ? (
          <ClozeFace note={note} revealed={revealed} />
        ) : card.direction === "production" ? (
          <>
            <div className="translation">{note.translation}</div>
            {revealed && (
              <>
                <div className="divider" />
                <div className="term answer">{note.term}</div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="term">{note.term}</div>
            {revealed && (
              <>
                <div className="divider" />
                <div className="translation">{note.translation}</div>
              </>
            )}
          </>
        )}

        {revealed && note.example && (
          <div className="example">{note.example}</div>
        )}
        {revealed && note.example_translation && (
          <div className="example-translation">{note.example_translation}</div>
        )}
        {revealed && note.note && <div className="usage">{note.note}</div>}

        {revealed && profile.audioEnabled && tts.hasVoice(lang) && (
          <button
            className="speak"
            aria-label="Speak"
            onClick={() => tts.speak(note.example || note.term, lang, { voiceURI: profile.voiceURI })}
          >
            🔊
          </button>
        )}
      </div>

      {revealed ? (
        <div className="answers">
          {([1, 2, 3, 4] as RatingValue[]).map((rating) => (
            <button
              key={rating}
              className="btn"
              data-rating={rating}
              onClick={() => void answer(rating)}
            >
              <span className="label">{RATING_LABELS[rating]}</span>
              <span className="interval">{intervals?.[rating]}</span>
            </button>
          ))}
        </div>
      ) : (
        <button className="btn primary full" onClick={() => setRevealed(true)}>
          Show answer
        </button>
      )}

      <div className="row" style={{ justifyContent: "space-between", marginTop: 12 }}>
        <button className="btn ghost small" onClick={finish}>End session</button>
        <span className="count k">{index} / {total}</span>
      </div>
    </div>
  );
}

function ClozeFace({ note, revealed }: { note: ReviewItem["note"]; revealed: boolean }) {
  const text = note.cloze_text ?? "";
  const answer = note.cloze_answer ?? "";
  const parts = text.split(/\{\{.*?\}\}/);
  return (
    <div className="cloze">
      {parts[0]}
      <span className="blank">{revealed ? answer : " ____ "}</span>
      {parts.slice(1).join("")}
    </div>
  );
}

/** What the speech synthesiser should read, or null when it would give the answer away. */
function speakableText(item: ReviewItem, revealed: boolean): string | null {
  const { card, note } = item;
  if (card.direction === "production" && !revealed) return null;
  if (card.direction === "cloze" && !revealed) return null;
  if (card.direction === "recognition" && !revealed) return note.term;
  return note.example || note.term;
}
